import gql from "graphql-tag";
import type { DocumentNode } from "graphql";
import { API, graphqlOperation } from "aws-amplify";
import { NolaApiInitializer, NolaNovelApiInitializer, nolaNovelApi } from "@/initializer";
import i18n from "@/lib/i18n";
import * as log from "@/lib/log";
import { showNotifyDialog } from "./dialog";
import { isClockSkewError, isForbiddenError } from "./utils/clockSkew";
import { isGraphqlMutation, isNetworkError, withNetworkRetry } from "./utils/networkRetry";

const help = process.env.VUE_APP_NOTION_HELP;

export type RequestParam = {
  query: string | DocumentNode;
  variables?: any;
  headers?: Record<string, string>;
};

function logGraphqlError(error: unknown) {
  log.error({ error }).catch((logError) => {
    console.error("Failed to send error log", logError);
  });
}

function errorDialogTitle(): string {
  return i18n.t("common.errors.title") as string;
}

function clockSkewContent(): string {
  return i18n.t("common.errors.clockSkew", { help }) as string;
}

function forbiddenContent(): string {
  return i18n.t("common.errors.forbidden") as string;
}

function networkErrorContent(): string {
  return i18n.t("common.errors.network", { help }) as string;
}

function genericErrorContent(): string {
  return i18n.t("common.errors.generic") as string;
}

function buildGraphqlErrorContent(error: any): string {
  if (isClockSkewError(error)) return clockSkewContent();
  if (isForbiddenError(error)) return forbiddenContent();
  if (isNetworkError(error)) return networkErrorContent();
  if (!error) return genericErrorContent();

  let content = genericErrorContent();

  if (error.errors) {
    const e = error.errors[0] || {};
    const { path } = e;
    let { message } = e;

    /** Error handling during subscribe with Stripe */
    if (path && path.includes("subscribe")) {
      if (message.includes("This promotion code has expired.")) {
        message = "このクーポンはご利用上限回数を超えています。";
      } else if (
        message.includes(
          "This promotion code cannot be redeemed because the associated customer has prior transactions."
        )
      ) {
        message = "このクーポンは初回利用限定のためご利用いただけません。";
      }
    }

    content += `<br />${message}`;
  }

  return content;
}

async function notifyGraphqlFailure(error: any) {
  await showNotifyDialog({
    title: errorDialogTitle(),
    content: buildGraphqlErrorContent(error),
  });
  logGraphqlError(error);
}

/**
 * ESLintのErrorが気になったので、Clientのクラスとして定義した
 * 影響範囲が大きいので元のexport functionは残しておく
 */
export default class GraphQLClient {
  static async request(params: RequestParam) {
    const isMutation = isGraphqlMutation(params.query);
    try {
      NolaApiInitializer.init(params.headers);

      const { data, errors } = <any>await withNetworkRetry(
        () => API.graphql(graphqlOperation(params.query, params.variables)) as Promise<any>,
        { retry: !isMutation }
      );
      if (errors) {
        await notifyGraphqlFailure({ errors });
      }
      return data;
    } catch (error: any) {
      await notifyGraphqlFailure(error);
      throw error;
    }
  }
}

export class NolaNovelGraphQLClient {
  static async request(params: RequestParam) {
    const isMutation = isGraphqlMutation(params.query);
    try {
      NolaNovelApiInitializer.init();

      const { query, variables } = params;
      // Nolaノベル専用の APIClass インスタンス経由で GraphQL を呼び出す。
      // グローバルな `API` (Nola 用) の設定を上書きしないため、Nola と Nolaノベル の
      // 呼び出しが並列で走っても認証タイプ/エンドポイントが取り違わらない。
      const { data, errors } = <any>await withNetworkRetry(
        () => nolaNovelApi.graphql(graphqlOperation(query, variables)) as Promise<any>,
        { retry: !isMutation }
      );
      if (errors) await this.showError({ errors });

      return data;
    } catch (error: any) {
      await notifyGraphqlFailure(error);
      throw error;
    } finally {
      // `NolaNovelApiInitializer.init()` は `AuthService.configure` で
      // `clientMetadata: { serviceName: "Nolaノベル" }` を設定する。これは
      // Cognito の CustomMessage Lambda Trigger (サインアップ / パスワードリセット等のメール)
      // が Nola と Nolaノベル の送信元を出し分けるために参照するグローバル状態なので、
      // Nolaノベル 呼び出し後に Nola 側へ戻しておく必要がある。
      // AppSync 用 `nolaNovelApi` / S3 用 `nolaNovelStorage` は `initializer.ts` の
      // モジュール読み込み時に configure 済みで、こちらは切り替え対象ではない。
      NolaApiInitializer.init();
    }
  }

  static async showError(error?: any) {
    await showNotifyDialog({
      title: errorDialogTitle(),
      content: buildGraphqlErrorContent(error),
    });
  }
}

export const novel = gql`
  query ($id: ID!) {
    novel(id: $id) {
      title
      description
    }
  }
`;

export async function callApi(query: string | DocumentNode, variables?: any) {
  const isMutation = isGraphqlMutation(query);
  try {
    NolaApiInitializer.init();

    const { data, errors } = <any>await withNetworkRetry(
      () => API.graphql(graphqlOperation(query, variables)) as Promise<any>,
      {
        retry: !isMutation,
      }
    );
    if (errors) {
      await notifyGraphqlFailure({ errors });
    }
    return data;
  } catch (error: any) {
    await notifyGraphqlFailure(error);
    throw error;
  }
}
